"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildMuiOverridesAndComponents = void 0;
const createBreakpoints_1 = __importDefault(require("@material-ui/core/styles/createBreakpoints"));
const buildColorsByBrand_1 = require("./buildColorsByBrand");
const fonts_1 = require("./fonts");
const buildMuiOverridesAndComponents = (brandIdentity, useNotoFontFamily = false) => {
    const generatedColorData = (0, buildColorsByBrand_1.buildColorsByBrand)(brandIdentity);
    const fontFamilySet = useNotoFontFamily ? fonts_1.notoFontFamily : fonts_1.latoFontFamily;
    const Colors = generatedColorData.colors;
    const breakpoints = (0, createBreakpoints_1.default)({});
    const cssBaselineStyles = {
        html: {
            height: '100%',
        },
        body: {
            height: '100%',
        },
        '#__next': {
            height: '100%',
        },
        'h1, h2, h3, h4, h5, h6, p': {
            margin: 0,
        },
        '.DraftEditor-root': {
            '& h1, h2': {
                color: Colors.SecondaryMain,
            },
            '& a': {
                color: Colors.PrimaryMain,
            },
        },
        a: {
            color: Colors.PrimaryMain,
            fontWeight: fonts_1.FontWeights.Black,
            textDecoration: 'none',
            '&:hover': {
                color: Colors.PrimaryDark,
            },
        },
        i: {
            textStyle: 'italic',
        },
        em: {
            textStyle: 'italic',
        },
        b: {
            fontWeight: fonts_1.FontWeights.Bold,
        },
        strong: {
            fontWeight: fonts_1.FontWeights.Bold,
        },
        small: {
            fontSize: '0.75rem', // 12px. 12/16 = 0.75
            lineHeight: 0.9375,
        },
        '.RichText_ContentBlock_text-align-right': {
            textAlign: 'right',
        },
        '.RichText_ContentBlock_text-align-center': {
            textAlign: 'center',
        },
        '.RichText_Image-center': {
            marginLeft: 'auto',
            marginRight: 'auto',
        },
        '.public-DraftStyleDefault-unorderedListItem.public-DraftStyleDefault-depth1': {
            listStyleType: 'circle',
        },
        '.public-DraftStyleDefault-listLTR.public-DraftStyleDefault-depth1': {
            marginLeft: '1.4em',
        },
    };
    const muiTabRoot = {
        fontWeight: fonts_1.FontWeights.Bold,
        '&.Mui-focusVisible': {
            outline: 'revert',
        },
    };
    const muiContainerRoot = {
        paddingLeft: 16,
        paddingRight: 16,
        [breakpoints.up('sm')]: {
            paddingLeft: 40,
            paddingRight: 40,
        },
    };
    const muiButtonBaseRoot = {
        fontFamily: fontFamilySet,
        '&.Mui-focusVisible': {
            outline: 'revert',
        },
    };
    const muiButtonRoot = {
        fontSize: '1rem',
        fontWeight: fonts_1.FontWeights.Bold,
        textTransform: 'none', // Simple 'none' string breaks TextTransformProperties enum lookup on CSSProperties which differs between MUI4 and MUI5. Hard setting to 'none' resolves to the enum correctly.
        letterSpacing: 'normal',
        paddingLeft: 24,
        paddingRight: 24,
        borderRadius: 5,
        minWidth: 144,
    };
    const muiButtonContained = {
        boxShadow: 'none',
        '&.Mui-focusVisible': {
            boxShadow: 'none',
        },
        '&:active': {
            boxShadow: 'none',
        },
        '&:hover': {
            boxShadow: 'none',
            color: Colors.SecondaryText,
        },
        '&.Mui-disabled': {
            backgroundColor: Colors.Grey500,
            color: Colors.SecondaryText,
        },
    };
    const muiButtonOutlined = {
        '&.Mui-disabled': {
            color: Colors.Grey500,
            borderColor: Colors.Grey500,
        },
    };
    const muiButtonText = {
        color: Colors.PrimaryMain,
        '&:hover': {
            color: Colors.PrimaryDark,
            backgroundColor: 'transparent',
        },
        '&:focus': {
            color: Colors.PrimaryDark,
        },
    };
    const muiButtonTextPrimary = {
        '&:hover': {
            backgroundColor: 'transparent',
        },
    };
    const muiButtonTextSecondary = {
        '&:hover': {
            color: Colors.SecondaryDark,
            backgroundColor: 'transparent',
        },
    };
    const muiButtonOutlinedPrimary = {
        backgroundColor: Colors.White,
        borderColor: Colors.PrimaryMain,
        '&:hover': {
            backgroundColor: Colors.White,
            borderColor: Colors.PrimaryDark,
            color: Colors.PrimaryDark,
        },
    };
    const muiButtonContainedPrimary = {
        '&.Mui-focusVisible': {
            boxShadow: '0 0 6px 0 rgba(15, 105, 175, 0.5), inset 0 0 0 2px #ffffff',
        },
    };
    const muiButtonContainedSecondary = {
        color: Colors.SecondaryText,
        '&.Mui-focusVisible': {
            boxShadow: `0 0 6px 0 rgba(80, 50, 145, 0.5), inset 0 0 0 2px #ffffff`,
        },
    };
    const muiButtonOutlinedSecondary = {
        '&:hover': {
            backgroundColor: 'transparent',
            borderColor: Colors.SecondaryDark,
            color: Colors.SecondaryDark,
        },
        '&.Mui-disabled': {
            borderColor: Colors.Grey500,
        },
    };
    const muiButtonSizeLarge = {
        fontSize: '1rem',
        padding: '10px 24px',
        '&.Mui-outlinedSizeLarge': {
            padding: '9px 24px',
            [breakpoints.up('md')]: {
                padding: '5px 24px',
            },
        },
    };
    const muiSvgIconRoot = {
        fontSize: '1.25rem',
    };
    const muiSvgIconColorDisabled = {
        color: Colors.Grey500,
    };
    const muiCheckboxRoot = {
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '&.Mui-checked': {
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
    };
    const muiCheckboxColorPrimary = {
        '&.Mui-checked': {
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
    };
    const muiCheckboxColorSecondary = {
        '&.Mui-checked': {
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
    };
    const muiChipColorSecondary = {
        backgroundColor: 'rgba(6, 170, 102, 0.1)',
        color: Colors.Grey800,
        fontWeight: fonts_1.FontWeights.Bold,
    };
    const muiMenuItemRoot = {
        '&:hover': {
            backgroundColor: Colors.PrimaryLight,
        },
        '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: Colors.PrimaryLight,
        },
    };
    const muiSwitchSwitchBase = {
        color: Colors.Grey50,
    };
    // MUI4 Overrides
    const mui4Overrides = {
        MuiCssBaseline: {
            '@global': cssBaselineStyles,
        },
        MuiTab: {
            root: muiTabRoot,
        },
        MuiContainer: {
            root: muiContainerRoot,
        },
        MuiButtonBase: {
            root: muiButtonBaseRoot,
        },
        MuiButton: {
            root: muiButtonRoot,
            contained: muiButtonContained,
            outlined: muiButtonOutlined,
            text: muiButtonText,
            textPrimary: muiButtonTextPrimary,
            textSecondary: muiButtonTextSecondary,
            outlinedPrimary: muiButtonOutlinedPrimary,
            containedPrimary: muiButtonContainedPrimary,
            containedSecondary: muiButtonContainedSecondary,
            outlinedSecondary: muiButtonOutlinedSecondary,
            sizeLarge: muiButtonSizeLarge,
        },
        MuiSvgIcon: {
            root: muiSvgIconRoot,
            colorDisabled: muiSvgIconColorDisabled,
        },
        MuiCheckbox: {
            root: muiCheckboxRoot,
            colorPrimary: muiCheckboxColorPrimary,
            colorSecondary: muiCheckboxColorSecondary,
        },
        MuiChip: {
            colorSecondary: muiChipColorSecondary,
        },
        MuiMenuItem: {
            root: muiMenuItemRoot,
        },
        MuiSwitch: {
            switchBase: muiSwitchSwitchBase,
        },
    };
    // MUI5 Components
    const mui5Components = {
        MuiCssBaseline: {
            styleOverrides: cssBaselineStyles,
        },
        MuiTab: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: muiTabRoot,
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: muiContainerRoot,
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: muiButtonBaseRoot,
            },
        },
        MuiButton: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: muiButtonRoot,
                contained: muiButtonContained,
                outlined: muiButtonOutlined,
                text: muiButtonText,
                textPrimary: muiButtonTextPrimary,
                textSecondary: muiButtonTextSecondary,
                outlinedPrimary: muiButtonOutlinedPrimary,
                containedPrimary: muiButtonContainedPrimary,
                containedSecondary: muiButtonContainedSecondary,
                outlinedSecondary: muiButtonOutlinedSecondary,
                sizeLarge: muiButtonSizeLarge,
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: muiSvgIconRoot,
                colorDisabled: muiSvgIconColorDisabled,
            },
        },
        MuiCheckbox: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: muiCheckboxRoot,
                colorPrimary: muiCheckboxColorPrimary,
                colorSecondary: muiCheckboxColorSecondary,
            },
        },
        MuiLink: {
            defaultProps: {
                underline: 'none',
            },
        },
        MuiChip: {
            styleOverrides: {
                colorSecondary: muiChipColorSecondary,
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: muiMenuItemRoot,
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: muiMenuItemRoot,
            },
        },
        MuiIconButton: {
            defaultProps: {
                disableRipple: true,
            },
        },
    };
    return {
        muiV4: mui4Overrides,
        muiV5: mui5Components,
    };
};
exports.buildMuiOverridesAndComponents = buildMuiOverridesAndComponents;
//# sourceMappingURL=overridesUpgradeMixer.js.map